import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Lock, Mail, Loader2, Eye, EyeOff, AlertCircle, Car } from 'lucide-react';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.email || !form.password) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    setLoading(true);
    try {
      // Usar endpoint específico de admin
      const res = await axios.post(`${API}/admin/login`, {
        email: form.email.toLowerCase().trim(),
        password: form.password
      });
      
      login(res.data.token, res.data.user);
      toast.success('Login realizado com sucesso!');
      navigate('/control-portal-clube/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao fazer login';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1F3A] flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-2xl border-[#E2E8F0]">
        {/* Header */}
        <div className="flex items-center justify-center mb-6">
          <div className="bg-[#2563EB] p-4 rounded-xl">
            <Car className="w-10 h-10 text-white" />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-center mb-1 text-[#0B1F3A]">JATOO</h1>
        <p className="text-center text-slate-500 mb-6">Painel Administrativo</p>

        {/* Alerta de acesso restrito */}
        <Alert className="mb-6 border-[#F97316]/30 bg-[#F97316]/5">
          <AlertCircle className="h-4 w-4 text-[#F97316]" />
          <AlertDescription className="text-[#F97316] text-sm">
            Área restrita. Apenas administradores autorizados.
          </AlertDescription>
        </Alert>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">E-mail</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="email"
                type="email"
                placeholder="admin@jatoo.app.br"
                className="pl-10 border-[#E2E8F0] focus:border-[#2563EB] focus:ring-[#2563EB]"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                data-testid="admin-email-input"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="password">Senha</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                className="pl-10 pr-10 border-[#E2E8F0] focus:border-[#2563EB] focus:ring-[#2563EB]"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                data-testid="admin-password-input"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          
          <Button 
            type="submit" 
            className="w-full bg-[#2563EB] hover:bg-[#2563EB]/90" 
            disabled={loading}
            data-testid="admin-login-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Verificando...
              </>
            ) : (
              <>
                <Shield className="w-4 h-4 mr-2" />
                Entrar no Painel
              </>
            )}
          </Button>
        </form>

        <Button 
          variant="ghost" 
          onClick={() => navigate('/')} 
          className="w-full mt-4 text-slate-500 hover:text-[#0B1F3A]"
        >
          Voltar para Home
        </Button>
        
        {/* Credenciais padrão (modo dev) */}
        <div className="mt-6 pt-4 border-t border-[#E2E8F0]">
          <p className="text-xs text-center text-slate-500">
            Credenciais padrão: <br />
            <code className="bg-slate-100 px-2 py-0.5 rounded text-[#0B1F3A]">admin@jatoo.app.br</code> / <code className="bg-slate-100 px-2 py-0.5 rounded text-[#0B1F3A]">Admin@123</code>
          </p>
        </div>
      </Card>
    </div>
  );
}
